import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  FolderOpen,
  CreditCard,
  Video,
  User,
  Menu,
  X,
  Wrench,
  LifeBuoy,
  LogOut,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "../lib/useQuery";
import { getStudent } from "../lib/queries";
import logo from "../assets/logo-blanco-horizontal-etc.png";

const NAV_ITEMS = [
  { to: "/", icon: LayoutDashboard, label: "Panel Principal", tour: "dashboard" },
  { to: "/actividades", icon: ClipboardList, label: "Mis Actividades", tour: "activities" },
  { to: "/herramientas", icon: Wrench, label: "Herramientas", tour: "tools" },
  { to: "/documentos", icon: FolderOpen, label: "Documentos", tour: "documents" },
  { to: "/pagos", icon: CreditCard, label: "Pagos", tour: "payments" },
  { to: "/sesiones", icon: Video, label: "Sesiones", tour: "sessions" },
  { to: "/perfil", icon: User, label: "Mi Perfil", tour: "profile" },
];

const EMERGENCY_ITEM = {
  to: "/emergencia",
  icon: LifeBuoy,
  label: "Llamada de Emergencia",
};

export default function Sidebar({ isOpen, onToggle }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { data: student } = useQuery(() => getStudent(user.id), [user.id]);

  // La Llamada de Emergencia solo aplica a alumnos con registro en Airtable.
  const isAirtable =
    student?.airtable_id && !student.airtable_id.startsWith("admin_");
  const navItems = isAirtable
    ? [...NAV_ITEMS.slice(0, 6), EMERGENCY_ITEM, ...NAV_ITEMS.slice(6)]
    : NAV_ITEMS;

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await signOut();
  };

  const name = student?.name ?? "Alumno";
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-dvh w-64 bg-navy flex flex-col z-50 transition-transform lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 border-b border-white/8 flex items-center justify-between">
          <img src={logo} alt="Entrena Tu Centro" className="h-20" />
          <button onClick={onToggle} className="lg:hidden text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map(({ to, icon: Icon, label, badge, tour }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-tour={tour}
              onClick={() => { if (window.innerWidth < 1024) onToggle(); }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-brand-accent/15 text-brand-accent"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={18} />
              <span>{label}</span>
              {badge && (
                <span className="ml-auto bg-brand-accent text-white text-[0.65rem] font-semibold px-2 py-0.5 rounded-full">
                  {badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-2">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:bg-white/5 hover:text-red-400 transition-all"
          >
            <LogOut size={18} />
            <span>Cerrar Sesión</span>
          </button>
        </div>

        <div className="px-5 py-4 border-t border-white/8 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand to-brand-accent flex items-center justify-center text-white font-bold text-sm">
            {initials}
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{name}</p>
            <p className="text-[0.7rem] text-gray-400">{student?.program ?? ""}</p>
          </div>
        </div>
      </aside>
    </>
  );
}

export function MobileMenuButton({ onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="lg:hidden w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-gray-300 transition-all"
    >
      <Menu size={18} />
    </button>
  );
}
